import moment from "moment";
import numbro from "numbro";

function isBinaryMeasureType(measureType) {
  return typeof measureType === "string" && measureType.includes("boolean");
}

export function fillBlankData(chartData, dateInterval) {
  if (!chartData || chartData.length === 0) {
    return chartData;
  }

  if (
    !dateInterval ||
    dateInterval.length === 0 ||
    !dateInterval[0] ||
    !dateInterval[1]
  ) {
    return chartData;
  }

  const startDate = moment(dateInterval[0]).unix();
  const endDate = moment(dateInterval[1]).unix();
  let dataWithMargin = [...chartData];

  if (startDate < dataWithMargin[0].created) {
    let currTime = startDate;
    let generatedData = [];
    while (currTime < dataWithMargin[0].created) {
      generatedData.push({ created: currTime, value: null });
      currTime += 30 * 60;
    }
    dataWithMargin = [...generatedData, ...dataWithMargin];
  }

  if (endDate > dataWithMargin[dataWithMargin.length - 1].created) {
    let currTime = dataWithMargin[dataWithMargin.length - 1].created + 30 * 60;
    let generatedData = [];
    while (currTime <= endDate) {
      generatedData.push({ created: currTime, value: null });
      currTime += 30 * 60;
    }
    dataWithMargin = [...dataWithMargin, ...generatedData];
  }

  return dataWithMargin;
}

export function detectGaps(records) {
  if (!records || records.length < 2) {
    return [];
  }

  const gapArray = [];

  records.reduce((previous, current) => {
    if (
      moment(current.created * 1000).diff(previous.created * 1000, "hours") > 1
    ) {
      gapArray.push({
        from: previous.created,
        to: current.created,
      });
    }
    return current;
  }, records[0]);

  return gapArray;
}

export function detectGapsWithThreshold(records, thresholdHours = 1) {
  if (!records || records.length < 2) {
    return [];
  }

  const gapArray = [];

  records.reduce((previous, current) => {
    const hoursDiff = moment(current.created * 1000).diff(
      previous.created * 1000,
      "hours",
      true,
    );
    if (hoursDiff > thresholdHours) {
      gapArray.push({
        from: previous.created,
        to: current.created,
      });
    }
    return current;
  }, records[0]);

  return gapArray;
}

function binarySearchBefore(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  let result = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid].created <= target) {
      result = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return result;
}

function binarySearchAfter(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  let result = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid].created >= target) {
      result = mid;
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  return result;
}

export function alignRecordsWithLocation(locationData, sensorRecords, measureType = "") {
  if (!locationData || locationData.length === 0) {
    return sensorRecords;
  }

  if (!sensorRecords || sensorRecords.length === 0) {
    return [];
  }

  const firstSensorTime = sensorRecords[0].created;
  const lastSensorTime = sensorRecords[sensorRecords.length - 1].created;

  return locationData.map(({ created }) => {
    if (created < firstSensorTime || created > lastSensorTime) {
      return {
        created,
        value: null,
      };
    }

    const beforeIndex = binarySearchBefore(sensorRecords, created);
    const afterIndex = binarySearchAfter(sensorRecords, created);

    if (beforeIndex === -1) {
      return {
        created,
        value: null,
      };
    }

    if (afterIndex === -1) {
      return {
        created,
        value: null,
      };
    }

    if (
      beforeIndex === afterIndex ||
      sensorRecords[beforeIndex].created === created
    ) {
      return {
        created,
        value: sensorRecords[beforeIndex].value,
      };
    }

    const before = sensorRecords[beforeIndex];
    const after = sensorRecords[afterIndex];

    if (isBinaryMeasureType(measureType)) {
      return {
        created,
        value: before.value,
      };
    }

    const timeDiff = after.created - before.created;
    const ratio = (created - before.created) / timeDiff;
    const interpolatedValue =
      before.value + ratio * (after.value - before.value);

    return {
      created,
      value: Number(interpolatedValue.toFixed(1)),
    };
  });
}

export function splitRecordsIntoSegments(records, gaps) {
  if (!records || records.length === 0) {
    return [];
  }

  if (!gaps || gaps.length === 0) {
    return [records];
  }

  const segments = [];
  let currentSegment = [];

  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    const prevRecord = i > 0 ? records[i - 1] : null;

    const hasGapBefore =
      prevRecord &&
      gaps.some(
        (gap) => gap.from === prevRecord.created && gap.to === record.created,
      );

    if (hasGapBefore && currentSegment.length > 0) {
      segments.push(currentSegment);
      currentSegment = [];
    }

    currentSegment.push(record);
  }

  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}

export function buildSegmentsWithGaps(records, gaps) {
  const segments = splitRecordsIntoSegments(records, gaps);

  let result = [];
  for (let i = 0; i < segments.length; i++) {
    result = result.concat(segments[i]);
    if (i < segments.length - 1) {
      result.push({ created: undefined, value: null });
    }
  }
  return result;
}

export function convertISOtoUnix(isoDate) {
  return moment(isoDate).unix();
}

export function normalizeRecords(records) {
  if (!records || records.length === 0) {
    return [];
  }

  return records.map((record) => {
    const unformattedValue = numbro.unformat(String(record.value));
    return {
      ...record,
      created:
        typeof record.created === "string"
          ? convertISOtoUnix(record.created)
          : record.created,
      value: unformattedValue === undefined ? NaN : unformattedValue,
    };
  });
}

export function processChartData({
  sensorsData,
  locationData = [],
  dateInterval = [],
  formatValueFn = null,
  maxGapHours = 1,
}) {
  if (!sensorsData || sensorsData.length === 0) {
    if (locationData.length === 0) {
      return [
        {
          type: "scatter",
          mode: "lines",
          connectgaps: false,
          name: "",
          uuid: "",
          x: [],
          y: [],
        },
      ];
    }
  }

  const normalizedLocationData =
    locationData.length > 0 ? normalizeRecords(locationData) : [];

  const sensorTraces = sensorsData.map((sensorItem) => {
    let records = normalizeRecords(sensorItem.records || []);
    if (records.length === 0) {
      return {
        type: "scatter",
        mode: "lines",
        connectgaps: false,
        name: sensorItem.name || "",
        uuid: sensorItem.uuid || "",
        line: { color: sensorItem.color },
        x: [],
        y: [],
      };
    }

    records = records.filter(
      (item) =>
        typeof item.created === "number" &&
        !isNaN(item.created) &&
        !isNaN(item.value),
    );

    records = records.sort((a, b) => a.created - b.created);

    const gaps = detectGapsWithThreshold(records, maxGapHours);

    let finalRecords = [];

    if (normalizedLocationData.length > 0 && gaps.length > 0) {
      const segments = splitRecordsIntoSegments(records, gaps);

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];

        if (segment.length === 0) continue;

        const segmentStart = segment[0].created;
        const segmentEnd = segment[segment.length - 1].created;

        const relevantLocationData = normalizedLocationData.filter(
          (loc) => loc.created >= segmentStart && loc.created <= segmentEnd,
        );

        // NOTE: Only interpolate if location data adds points
        let segmentRecords;
        if (relevantLocationData.length > segment.length) {
          segmentRecords = alignRecordsWithLocation(
            relevantLocationData,
            segment,
            (sensorItem.sensor && sensorItem.sensor.measure_type) || "",
          );
        } else {
          segmentRecords = segment;
        }

        finalRecords = finalRecords.concat(segmentRecords);

        if (i < segments.length - 1) {
          finalRecords.push({ created: undefined, value: null });
        }
      }
    } else if (normalizedLocationData.length > records.length) {
      finalRecords = alignRecordsWithLocation(normalizedLocationData, records, (sensorItem.sensor && sensorItem.sensor.measure_type) || "");
    } else if (normalizedLocationData.length > 0) {
      finalRecords = records;
    } else {
      finalRecords = buildSegmentsWithGaps(records, gaps);
    }

    finalRecords = fillBlankData(finalRecords, dateInterval);

    let runStart = 0;
    for (let i = 0; i <= finalRecords.length; i++) {
      const isBoundary =
        i === finalRecords.length || finalRecords[i].value === null;
      if (isBoundary) {
        if (i - runStart === 1) {
          finalRecords[runStart] = { ...finalRecords[runStart], isolated: true };
        }
        runStart = i + 1;
      }
    }

    const markerSizes = finalRecords.map((record) => (record.isolated ? 5 : 0));

    let formattedDataForHoverTemplate = null;
    if (formatValueFn && sensorItem.sensor) {
      formattedDataForHoverTemplate = finalRecords.map((element) =>
        formatValueFn(element.value, sensorItem.sensor.measure_type),
      );
    }

    // NOTE: Gap records have undefined created and null value, which breaks the line
    return {
      type: "scatter",
      mode: "lines+markers",
      hovertemplate: formattedDataForHoverTemplate,
      connectgaps: false,
      name: sensorItem.name || "",
      uuid: sensorItem.uuid || "",
      line: { color: sensorItem.color, shape: "hv" },
      marker: { color: sensorItem.color, size: markerSizes },
      x: finalRecords.map((record) => {
        return typeof record.created === "number"
          ? moment(record.created * 1000).format()
          : null;
      }),
      y: finalRecords.map((record) => record.value),
    };
  });

  // This approach was selected because we couldn't get rid of the double tooltip problem
  // (Plotly doesn't support having the tooltip on for one axis - either both or none)
  // This "ghost" trace ensures the @hover event fires even when sensor data is null or missing.
  // We set marker size to 0 and name it "HIDE_LOCATION" to provide a unique hook for the CSS to find.
  // By fixing Y at 0, we create a stable anchor point that allows the X-unified hover to track the timeline.
  if (normalizedLocationData.length > 0) {
    const hoverTrace = {
      type: "scatter",
      mode: "markers",
      name: "HIDE_LOCATION",
      showlegend: false,
      x: normalizedLocationData.map((loc) => {
        return loc.created ? moment(loc.created * 1000).format() : null;
      }),
      y: normalizedLocationData.map(() => 0),
      marker: {
        size: 0,
        color: "transparent",
      },
      hovertemplate: "HIDE_LOCATION",
      hoverlabel: {
        bgcolor: "transparent",
        bordercolor: "transparent",
        font: { size: 0 },
      },
    };
    sensorTraces.push(hoverTrace);
  }

  return sensorTraces;
}
